/**
 * Máscara de documento fiscal (CPF/CNPJ) — primitivo compartilhado.
 *
 * Vive em `shared/` porque três features já dependem dele: cadastro de cliente
 * (CRM), criação rápida de cliente no chat, e cadastro de unidade. Ficava em
 * `modules/crm/customers/utils/` e era importado de fora do módulo, o que é
 * acoplamento entre features — todo papel de UI tem UM primitivo shared dono.
 *
 * É só cosmético: o backend normaliza `document` para dígitos puros antes de
 * validar tamanho (11 = CPF, 14 = CNPJ), então o valor mascarado pode ser
 * enviado direto — mas os formulários mandam dígitos por clareza.
 */

const onlyDigits = (value: string): string => value.replace(/\D/g, '');

/**
 * Aplica máscara progressiva conforme o usuário digita: CPF (11 dígitos,
 * `000.000.000-00`) para `type: 'PF'`, CNPJ (14 dígitos,
 * `00.000.000/0000-00`) para `type: 'PJ'`. Trunca no tamanho máximo do tipo
 * — reaplicar ao trocar de tipo (PJ → PF) corta os dígitos excedentes.
 */
export function formatDocumentInput(value: string, type: 'PF' | 'PJ'): string {
  const maxLen = type === 'PF' ? 11 : 14;
  const digits = onlyDigits(value).slice(0, maxLen);

  if (type === 'PF') {
    return digits
      .replace(/(\d{3})(\d)/, '$1.$2')
      .replace(/(\d{3})(\d)/, '$1.$2')
      .replace(/(\d{3})(\d{1,2})$/, '$1-$2');
  }

  return digits
    .replace(/(\d{2})(\d)/, '$1.$2')
    .replace(/(\d{3})(\d)/, '$1.$2')
    .replace(/(\d{3})(\d)/, '$1/$2')
    .replace(/(\d{4})(\d{1,2})$/, '$1-$2');
}

/** Dígitos puros do documento — usado para validação client-side e no payload. */
export function documentDigitsOnly(value: string): string {
  return onlyDigits(value);
}

/** Label do documento conforme o tipo de cliente ('CPF' para PF, 'CNPJ' para PJ). */
export function documentLabel(type: 'PF' | 'PJ'): string {
  return type === 'PF' ? 'CPF' : 'CNPJ';
}
